// Neon Arcade - Cyberpunk neon greens and purples
// Key colors:
//   Void Black #0A0A1A - backgrounds, headers, dark UI
//   Neon Green #39FF14 - CTAs, highlights, action buttons
//   Electric Purple #B026FF - secondary elements, accents
//   Cyan Glow #00FFFF - success states, availability indicators
//   Dark Chrome #1A1A2E - card backgrounds, panels
//   Hot Pink #FF2E97 - accents, badges, notification dots

export const lightTheme = {
  name: 'light',

  /* === Brand === */
  primaryColor: '#7C3AED', // Vivid Purple - CTAs (softer for light mode)
  primaryDark: '#6D28D9', // Deeper purple - hover states
  secondaryColor: '#10B981', // Soft Green - secondary elements

  successColor: '#10B981', // Green - availability, success
  dangerColor: '#ef4444', // Red - errors, destructive actions
  warningColor: '#f59e0b', // Amber - warnings
  infoColor: '#7C3AED', // Purple - informational

  /* === Backgrounds === */
  bgPrimary: '#F5F3FF', // Pale violet - main background
  bgSecondary: '#EDE9FE', // Light violet
  bgTertiary: '#DDD6FE', // Soft violet

  background: '#DDD6FE', // Page background
  cardBackground: '#ffffff', // White cards
  hoverBackground: '#EDE9FE', // Violet hover

  /* === Text === */
  textPrimary: '#1E1B4B', // Deep indigo
  textSecondary: '#4C1D95', // Dark violet
  textMuted: '#6D28D9', // Muted violet

  /* === Borders & Shadows === */
  borderColor: '#C4B5FD', // Soft violet border

  shadow: '0 1px 3px 0 rgba(30, 27, 75, 0.18), 0 1px 2px 0 rgba(0, 0, 0, 0.14)',
  shadowLg:
    '0 10px 25px -5px rgba(124, 58, 237, 0.35), 0 8px 10px -6px rgba(0, 0, 0, 0.35)',

  primaryHover: '#6D28D9', // --primary-hover
  primaryStrong: '#4C1D95', // --primary-strong (deep indigo, gradient endpoint)
  accentColor: '#10B981', // --accent-color (Soft Green)
  shadowMd:
    '0 4px 12px -2px rgba(30, 27, 75, 0.22), 0 2px 6px -1px rgba(0, 0, 0, 0.18)', // --shadow-md
  shadowXl:
    '0 20px 50px -10px rgba(124, 58, 237, 0.4), 0 12px 20px -8px rgba(0, 0, 0, 0.4)', // --shadow-xl
  shadowSoft: 'rgba(124, 58, 237, 0.12)', // --shadow-soft

  /* === Messaging / States === */
  primaryLight: 'rgba(124, 58, 237, 0.14)', // Purple tint
  unreadBackground: 'rgba(124, 58, 237, 0.06)',
  messageBubbleBg: '#EDE9FE',
  messageReceivedBg: '#ffffff',
  dangerLight: 'rgba(239, 68, 68, 0.1)',
};

export const darkTheme = {
  name: 'dark',

  /* === Brand === */
  primaryColor: '#39FF14', // Neon Green - CTAs, highlights
  primaryDark: '#2CD410', // Deeper neon green - hover states
  secondaryColor: '#B026FF', // Electric Purple - secondary elements

  successColor: '#00FFFF', // Cyan Glow - availability, success
  dangerColor: '#FF2E97', // Hot Pink - errors, destructive actions
  warningColor: '#FFE600', // Neon Yellow - warnings
  infoColor: '#B026FF', // Electric Purple - informational

  /* === Backgrounds === */
  bgPrimary: '#0A0A1A', // Void Black
  bgSecondary: '#12122A', // Deep void
  bgTertiary: '#1A1A2E', // Dark Chrome

  background: '#0A0A1A', // Page background
  cardBackground: '#12122A', // Deep void cards
  hoverBackground: '#1A1A2E', // Hover

  /* === Text === */
  textPrimary: '#E0FFE0', // Pale neon green
  textSecondary: '#D4A0FF', // Light lavender-purple (~7:1 contrast on #12122A)
  textMuted: '#8B5CF6', // Muted purple

  /* === Borders & Shadows === */
  borderColor: '#2A2A4A', // Dark chrome border

  shadow: '0 1px 3px 0 rgba(57, 255, 20, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.8)',
  shadowLg:
    '0 18px 35px -10px rgba(0, 0, 0, 0.9), 0 0 40px rgba(57, 255, 20, 0.25)',

  primaryHover: '#7AFF52', // --primary-hover
  primaryStrong: '#B0FF80', // --primary-strong (vivid neon, gradient endpoint)
  accentColor: '#B026FF', // --accent-color (Electric Purple)
  shadowMd:
    '0 4px 12px -2px rgba(57, 255, 20, 0.25), 0 2px 6px -1px rgba(0, 0, 0, 0.6)', // --shadow-md
  shadowXl:
    '0 28px 60px -14px rgba(0, 0, 0, 0.95), 0 0 60px rgba(57, 255, 20, 0.35)', // --shadow-xl
  shadowSoft: 'rgba(57, 255, 20, 0.2)', // --shadow-soft

  /* === Messaging / States === */
  primaryLight: 'rgba(57, 255, 20, 0.2)', // Neon Green tint
  unreadBackground: 'rgba(57, 255, 20, 0.1)',
  messageBubbleBg: '#1A1A2E',
  messageReceivedBg: '#222240',
  dangerLight: 'rgba(255, 46, 151, 0.25)',
};

export const themes = {
  light: lightTheme,
  dark: darkTheme,
};
